/**
 * Top-bar search box + Cmd/Ctrl+K command palette (#2477). Mounted into the
 * top bar's search slot from `App.tsx`, so `components/layout` never imports a
 * feature. Read-only: three GETs against local-D1 list endpoints.
 *
 * `cmdk` is imported directly: the kit's `Command` wrapper is a plain
 * `<input>` / `<div>` shell without cmdk's keyboard navigation.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '@resource-kit/ui/react';
import { usePermissions } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { jobStatusOptions } from '@features/dispatch';
import { paymentOptions } from '@features/orders';
import { MIN_SEARCH_CHARS } from './searchQueries';
import { useDebouncedValue } from './useDebouncedValue';
import { useGlobalSearch } from './useGlobalSearch';

const DEBOUNCE_MS = 250;
const IS_MAC = /Mac|iPhone|iPad/i.test(navigator.userAgent);

const itemClass =
  'flex items-center gap-3 px-3 py-2 rounded-md text-[13px] cursor-pointer data-[selected=true]:bg-surface-2';
const monoClass = 'font-mono text-foreground';
const subClass = 'text-muted-foreground';

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const can = {
    orders: permissions.includes(PERMISSIONS.ORDERS_READ),
    dispatch: permissions.includes(PERMISSIONS.DISPATCH_READ),
  };
  const allowed = can.orders || can.dispatch;

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setInput('');
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allowed]);

  const term = useDebouncedValue(input.trim(), DEBOUNCE_MS);
  const { orders, jobs, tasks } = useGlobalSearch(term, open, can);

  if (!allowed) return null;

  const go = (to: string) => {
    navigate(to);
    close();
  };

  const tooShort = input.trim().length < MIN_SEARCH_CHARS;
  const active = [can.orders && orders, can.dispatch && jobs, can.dispatch && tasks].filter(
    (q): q is typeof orders => q !== false,
  );
  const loading = !tooShort && active.some((q) => q.isLoading);
  const allEmpty =
    !tooShort &&
    !loading &&
    active.every((q) => !q.isError) &&
    !orders.data?.items.length &&
    !jobs.data?.items.length &&
    !tasks.data?.length;

  const group = (
    key: string,
    heading: string,
    q: { isError: boolean },
    rows: ReactNode[] | undefined,
  ) =>
    q.isError || (rows && rows.length > 0) ? (
      <Command.Group
        key={key}
        heading={heading}
        className="px-2 py-1 text-[11px] text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1"
      >
        {q.isError ? (
          <div role="alert" className="px-3 py-2 text-[13px] text-destructive">
            {t('search.groupError')}
          </div>
        ) : (
          rows
        )}
      </Command.Group>
    ) : null;

  const statusLabel = (
    value: string | null,
    options: { value: string | number; label: string }[],
  ) => {
    if (value === null) return '';
    const label = options.find((o) => o.value === value)?.label;
    return label ? t(label) : value;
  };

  const platformKbd = IS_MAC ? '⌘K' : 'Ctrl K';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="global-search-trigger"
        aria-label={t('search.ariaLabel')}
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 h-8 w-full min-w-0 lg:w-auto lg:min-w-[280px] px-2.5 rounded-lg border border-border bg-card text-[13px] text-muted-foreground cursor-pointer hover:text-foreground"
      >
        <span className="flex-1 min-w-0 truncate text-left">{t('search.placeholder')}</span>
        <kbd className="shrink-0 px-1.5 text-[11px] font-mono leading-[16px] rounded-sm border border-border border-b-2">
          {platformKbd}
        </kbd>
      </button>

      <Modal isOpen={open} onClose={close} size="lg" className="p-0 overflow-hidden">
        <Command shouldFilter={false} loop label={t('search.ariaLabel')}>
          <Command.Input
            autoFocus
            value={input}
            onValueChange={setInput}
            placeholder={t('search.placeholder')}
            className="w-full h-12 px-4 bg-transparent text-sm border-b border-border outline-none"
          />
          <Command.List className="max-h-[360px] overflow-auto p-1">
            {tooShort && (
              <div className="px-4 py-6 text-[13px] text-muted-foreground">
                {t('search.minChars')}
              </div>
            )}
            {loading && (
              <div className="px-4 py-3 text-[13px] text-muted-foreground">
                {t('search.loading')}
              </div>
            )}
            {allEmpty && (
              <div className="px-4 py-6 text-[13px] text-muted-foreground">{t('search.empty')}</div>
            )}
            {!tooShort &&
              can.orders &&
              group(
                'orders',
                t('search.group.orders'),
                orders,
                orders.data?.items.map((o) => (
                  <Command.Item
                    key={o.id}
                    value={`order:${o.id}`}
                    className={itemClass}
                    onSelect={() => go(`/orders/${o.id}`)}
                  >
                    <span className={monoClass}>{o.name}</span>
                    {o.delivery_date && <span className={subClass}>{o.delivery_date}</span>}
                    <span className={subClass}>
                      {statusLabel(o.financial_status, paymentOptions)}
                    </span>
                  </Command.Item>
                )),
              )}
            {!tooShort &&
              can.dispatch &&
              group(
                'jobs',
                t('search.group.jobs'),
                jobs,
                jobs.data?.items.map((j) => (
                  <Command.Item
                    key={j.id}
                    value={`job:${j.id}`}
                    className={itemClass}
                    onSelect={() => go(`/dispatch/jobs/${j.id}`)}
                  >
                    <span className={monoClass}>{j.name}</span>
                    <span className={subClass}>{statusLabel(j.status, jobStatusOptions)}</span>
                    <span className={subClass}>{j.shipping_date}</span>
                  </Command.Item>
                )),
              )}
            {!tooShort &&
              can.dispatch &&
              group(
                'tasks',
                t('search.group.tasks'),
                tasks,
                tasks.data?.map((k) => (
                  <Command.Item
                    key={k.taskId}
                    value={`task:${k.taskId}`}
                    className={itemClass}
                    onSelect={() => go(`/dispatch/jobs?search=${encodeURIComponent(k.shortId)}`)}
                  >
                    <span className={monoClass}>{k.shortId}</span>
                    {k.driverName && <span className={subClass}>{k.driverName}</span>}
                    <span className={subClass}>{t('search.taskJobs', { count: k.jobCount })}</span>
                  </Command.Item>
                )),
              )}
          </Command.List>
          <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground">
            {t('search.footer')}
          </div>
        </Command>
      </Modal>
    </>
  );
}
